import { IntlShape } from 'react-intl'

const reNumber = /^-?([0-9]{1,3}[,. ]?)*([,.]([0-9]+))?$/

export function normalizeNumberInput(
  value: string,
  { maximumFractionDigits = null }: { maximumFractionDigits: number | null } = {
    maximumFractionDigits: null,
  }
): string {
  const isNegativeSign = value[0] === '-'

  const inputValue = (isNegativeSign ? '-' : '') + value.replace(/[^.,0-9]/g, '')

  if (maximumFractionDigits) {
    const lastComma = Math.max(inputValue.lastIndexOf(','), inputValue.lastIndexOf('.'))
    // Enforce a maximum of 2 decimals in the field
    return lastComma !== -1 ? inputValue.slice(0, lastComma + maximumFractionDigits + 1) : inputValue
  }

  return inputValue
}

export function formatNumber(
  intl: IntlShape,
  value?: number,
  { multiplier = 1 }: { multiplier: number } = { multiplier: 1 }
) {
  return typeof value === 'number'
    ? `${intl.formatNumber(value / multiplier, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 10,
        useGrouping: false,
      })}`
    : ''
}

export function parseNumber(
  value: string,
  { maximumFractionDigits = null, multiplier = 1 }: { maximumFractionDigits: number | null; multiplier: number } = {
    maximumFractionDigits: null,
    multiplier: 1,
  }
): number | null {
  if (!value) return null

  if (typeof value !== 'string') {
    throw new TypeError(`Expected value to be a string, got ${typeof value}`)
  }

  const cleanedValue = value.replace(/^[,.]+|[,.]+$/, '')
  const isNegativeSign = value[0] === '-'

  const result = reNumber.exec(cleanedValue)
  // Check for accepted amount format
  if (result == null) return null

  // eslint-disable-next-line prefer-const
  let [decimal, ...thousandsRev] = cleanedValue
    .replace(/[\s]/g, '')
    .replace(/,/g, '.')
    .split('.')
    .reverse()

  const n = thousandsRev.length ? thousandsRev.reverse().join('') : decimal
  const d = thousandsRev.length ? (maximumFractionDigits ? decimal.slice(0, maximumFractionDigits) : decimal) : ''

  if (n === '-') return -0

  const integralPart = parseInt(n || '0', 10) * multiplier
  const fractionalPart = parseInt(d || '0', 10) * (multiplier / Math.pow(10, d.length))

  return isNegativeSign ? integralPart - fractionalPart : integralPart + fractionalPart
}
